/*
 * Copyright (C) 2021 DV Bern AG, Switzerland
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import {Location} from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
    inject
} from '@angular/core';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {TSInternePendenz} from '../../../models/TSInternePendenz';
import {EbeguUtil} from '../../../utils/EbeguUtil';
import {LogFactory} from '../../../utils/log-factory/LogFactory';
import {GesuchModelManager} from '../../service/gesuchModelManager';
import {InternePendenzDialogComponent} from './interne-pendenz-dialog/interne-pendenz-dialog.component';
import {InternePendenzenRS} from './internePendenzenRS.rest';
import {firstValueFrom} from 'rxjs';

const LOG = LogFactory.createLog('InternePendenzenComponent');

@Component({
    selector: 'interne-pendenzen-view',
    templateUrl: './interne-pendenzen.component.html',
    styleUrls: ['./interne-pendenzen.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class InternePendenzenComponent implements OnInit {
    private readonly location = inject(Location);
    private readonly dialog = inject(MatDialog);
    private readonly internePendenzenRS = inject(InternePendenzenRS);
    private readonly cd = inject(ChangeDetectorRef);
    private readonly gesuchModelManager = inject(GesuchModelManager);

    public internePendenzen: TSInternePendenz[] = [];

    public ngOnInit(): void {
        this.getPendenzen();
    }

    public getPendenzen(): void {
        this.internePendenzenRS
            .findInternePendenzenForGesuch(this.gesuchModelManager.getGesuch())
            .subscribe(
                internePendenzen => {
                    this.internePendenzen = internePendenzen;
                    this.cd.markForCheck();
                },
                error => this.handleError(error)
            );
    }

    public async addInternePendenz(): Promise<void> {
        let newPendenz = new TSInternePendenz();
        newPendenz.gesuch = this.gesuchModelManager.getGesuch();
        newPendenz = await this.openPendenzDialog(newPendenz);
        if (EbeguUtil.isNullOrUndefined(newPendenz)) {
            return;
        }
        this.internePendenzenRS.createInternePendenz(newPendenz).subscribe(
            savedPendenz => {
                // concat to change ref of element and trigger changeDetection of child
                this.internePendenzen = this.internePendenzen.concat([
                    savedPendenz
                ]);
                this.cd.markForCheck();
            },
            error => this.handleError(error)
        );
    }

    public async editPendenz(pendenz: TSInternePendenz): Promise<void> {
        const editedPendenz = await this.openPendenzDialog(pendenz);
        if (EbeguUtil.isNullOrUndefined(editedPendenz)) {
            return;
        }
        this.internePendenzenRS.updateInternePendenz(editedPendenz).subscribe(
            savedPendenz => {
                this.internePendenzen = this.internePendenzen.filter(
                    p => p.id !== editedPendenz.id
                );
                this.internePendenzen.push(savedPendenz);
                this.cd.markForCheck();
            },
            error => this.handleError(error)
        );
    }

    public deletePendenz(deletedPendenz: TSInternePendenz): void {
        this.internePendenzenRS.deleteInternePendenz(deletedPendenz).subscribe(
            () => {
                this.internePendenzen = this.internePendenzen.filter(
                    p => p.id !== deletedPendenz.id
                );
                this.cd.markForCheck();
            },
            error => this.handleError(error)
        );
    }

    public updateGesuchMarkiertFuerKontroll(value: boolean): void {
        this.gesuchModelManager.updateGesuchMarkiertFuerKontroll(value);
    }

    public navigateBack(): void {
        this.location.back();
    }

    private openPendenzDialog(
        internePendenz: TSInternePendenz
    ): Promise<TSInternePendenz> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = {internePendenz};
        dialogConfig.panelClass = 'interne-pendenzen-dialog';
        return firstValueFrom(
            this.dialog
                .open(InternePendenzDialogComponent, dialogConfig)
                .afterClosed()
        );
    }

    private handleError(error: Error): void {
        LOG.error(error);
    }
}
